import {
  Stack,
  Dropdown,
  Text,
  useTheme,
  Breadcrumb,
  IBreadcrumbItem,
  Icon,
  IDropdownOption,
} from '@fluentui/react';
import React from 'react';

import { StepCard } from './StepCard';
import { RamaButtons } from './RamaButtons';
import CategoriaButtons from './CategoriaButtons';
import { Club, ClubContext } from '../../system/SystemClient';
import {
  AppContext,
  CRITERIO_CATEGORIA,
  CRITERIO_FEDERACION,
  CRITERIO_RAMA,
  CRITERIO_TEMPORADA,
  MenuFilters,
} from '../../AppInRouterContext';
import { onRenderOptionCategoria } from '../Common/RenderOptions';
import { act } from 'react-dom/test-utils';

// === Icono simple ♀ / ♂ para el breadcrumb ===
const RamaIcon: React.FC<{ tipo: 'F' | 'M'; size?: number }> = ({ tipo, size = 18 }) => {
  const color = tipo === 'F' ? '#f472b6' : '#60a5fa';
  if (tipo === 'M') {
    return (
      <svg width={size} height={size} viewBox="0 0 24 24" aria-hidden focusable="false">
        <circle cx="10" cy="14" r="4" stroke={color} strokeWidth="2" fill="none" />
        <line x1="13" y1="11" x2="19" y2="5" stroke={color} strokeWidth="2" strokeLinecap="round" />
        <polyline
          points="15,5 19,5 19,9"
          fill="none"
          stroke={color}
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </svg>
    );
  }
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" aria-hidden focusable="false">
      <circle cx="12" cy="9" r="4" stroke={color} strokeWidth="2" fill="none" />
      <line x1="12" y1="13" x2="12" y2="20" stroke={color} strokeWidth="2" strokeLinecap="round" />
      <line x1="9" y1="17" x2="15" y2="17" stroke={color} strokeWidth="2" strokeLinecap="round" />
    </svg>
  );
};

const CrumbButton: React.FC<{
  title: string;
  onClick: (ev: React.MouseEvent<HTMLButtonElement>) => void;
  children: React.ReactNode;
}> = ({ title, onClick, children }) => {
  const [hovered, setHovered] = React.useState(false);
  const [pressed, setPressed] = React.useState(false);

  const style: React.CSSProperties = {
    fontFamily: 'inherit',
    fontWeight: '500',
    color: '#323232',
    fontSize: '14px',
    display: 'inline-flex',
    alignItems: 'center',
    gap: 2,
    padding: '10px 8px',
    borderRadius: 4,
    border: '1px solid #f2f7fb',
    background: hovered ? 'rgba(242, 247, 251, 0.35)' : 'transparent',
    boxShadow: hovered
      ? '0 2px 8px rgba(0,0,0,0.06), 0 0 0 3px rgba(242,247,251,0.6)'
      : '0 1px 3px rgba(0,0,0,0.04)',
    transform: pressed ? 'translateY(1px) scale(0.99)' : 'none',
    transition: 'background .20s ease, box-shadow .20s ease, transform .06s ease',
    cursor: 'pointer',
    outline: 'none',
  };

  return (
    <button
      type="button"
      aria-label={title}
      title={title}
      onClick={onClick}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => {
        setHovered(false);
        setPressed(false);
      }}
      onMouseDown={() => setPressed(true)}
      onMouseUp={() => setPressed(false)}
      onFocus={() => setHovered(true)}
      onBlur={() => setHovered(false)}
      style={style}
    >
      {children}
    </button>
  );
};

const Spacer = () => <div style={{ height: 8 }} />;

type StepKey = 'federacion' | 'temporada' | 'club' | 'rama' | 'categoria';

interface VerticalStackProps {
  showDropdownClubes: boolean;
  showDropdownTemporada: boolean;
  federacionDefault: string;

  clubId: string;
  setClubId: (v: string) => void;
  menuFilters: MenuFilters;
  onChange: (criterio: string, value: string) => void;
  onChangeFilters: (criterio: string, value: string) => void;

  clubes: Club[];
  temporadas: IDropdownOption<any>[];
  federaciones: IDropdownOption<any>[];
  ramas: IDropdownOption<any>[];
  categorias: IDropdownOption<any>[];

  clubHasRama: (id: string) => boolean | undefined;
  clubHasCategoria: (id: string) => boolean | undefined;
  clubHasCategoriaName: (id: string, text: string) => string;
  onRenderOptionCategoria: any;
  clubContext: ClubContext | undefined;
  activeDefault: string;
}

function VerticalFilterStack(props: VerticalStackProps) {
  const theme = useTheme();
  const federacionDefault = props.federacionDefault;

  const visibleSteps: StepKey[] = [
    !federacionDefault ? 'federacion' : null,
    props.showDropdownTemporada ? 'temporada' : null,
    props.showDropdownClubes ? 'club' : null,
    props.menuFilters.federacionId ? 'rama' : null,
    props.menuFilters.rama ? 'categoria' : null,
  ].filter(Boolean) as StepKey[];

  const onChange = (criterio: string, value: string): void => {
    props.onChangeFilters(criterio, value);
  };

  // ¿Está seleccionado cada paso?
  const isSelected = React.useCallback(
    (s: StepKey) => {
      switch (s) {
        case 'club':
          return !!props.clubId;
        case 'temporada':
          return !!props.menuFilters.temporadaId;
        case 'federacion':
          return !!props.menuFilters.federacionId;
        case 'rama':
          return !!props.menuFilters.rama;
        case 'categoria':
          return !!props.menuFilters.categoriaId;
        default:
          return false;
      }
    },
    [
      props.clubId,
      props.menuFilters.temporadaId,
      props.menuFilters.federacionId,
      props.menuFilters.rama,
      props.menuFilters.categoriaId,
    ]
  );

  // Primer paso pendiente (o undefined si está todo completo)
  const computeActive = (): StepKey | undefined => {
    const first = visibleSteps.find((s) => !isSelected(s));
    return first ?? undefined;
  };

  // Estado: paso activo (puede ser undefined)
  const [paso, setPaso] = React.useState<StepKey | undefined>('categoria');

  // ¿Todos completos?
  const allSelected = React.useMemo(
    () => visibleSteps.every((s) => isSelected(s)),
    [visibleSteps, isSelected]
  );

  // Resumen solo cuando realmente está todo completo
  const [summaryOnly, setSummaryOnly] = React.useState(false);
  React.useEffect(() => {
    if (allSelected) {
      const t = setTimeout(() => setSummaryOnly(true), 480);
      return () => clearTimeout(t);
    }
    setSummaryOnly(false);
  }, [allSelected]);

  // Recalcular activo si cambian filtros/visibilidades
  React.useEffect(() => {
    const next = computeActive();
    // Si el paso activo quedó oculto, cambialo al primero pendiente
    if (paso && !visibleSteps.includes(paso)) {
      setPaso(next);
      return;
    }
    // Si no hay activo y NO está todo completo, arranca por el primero pendiente
    if (!paso && !allSelected && next) {
      setPaso(next);
    }
  }, [visibleSteps, isSelected, paso, allSelected]);

  // Chevron: abrir ese paso o colapsar (deja sin activo)
  const toggleStep = (key: StepKey) => (nextOpen: boolean) => {
    setPaso(nextOpen ? key : undefined);
  };

  // Salir del resumen a un paso concreto (desde breadcrumb)
  const exitSummaryTo = (key: StepKey) => {
    setSummaryOnly(false);
    setPaso(key);
  };

  // Labels de resumen
  const resumen = {
    club: (() => {
      const opt = props.clubes?.find((c: any) => c.clubId === props.clubId);
      return opt ? opt.club : '';
    })(),
    temporada: (() => {
      const opt = props.temporadas?.find((t: any) => String(t.key) === String(props.menuFilters.temporadaId));
      return opt?.text ?? '';
    })(),
    federacion: (() => {
      const opt = props.federaciones?.find(
        (f: any) => String(f.key) === String(props.menuFilters.federacionId)
      );
      return opt?.text ?? '';
    })(),
    rama: (() => {
      const opt = props.ramas?.find((r: any) => String(r.key) === String(props.menuFilters.rama));
      return opt?.text ?? '';
    })(),
    categoria: (() => {
      const opt = props.categorias?.find((c: any) => String(c.key) === String(props.menuFilters.categoriaId));
      return opt ? props.clubHasCategoriaName(String(opt.key), opt.text) : '';
    })(),
  };

  // Breadcrumb
  const crumbs: IBreadcrumbItem[] = [];

  if (props.showDropdownTemporada) {
    const label = resumen.temporada || 'Temporada';
    crumbs.push({
      key: 'temporada',
      text: label,
      onClick: (ev, item) => {
        ev?.preventDefault();
        ev?.stopPropagation();
        exitSummaryTo('temporada');
      },
      onRender: () => (
        <CrumbButton
          title={label}
          onClick={(ev) => {
            ev.preventDefault();
            ev.stopPropagation();
            exitSummaryTo('temporada');
          }}
        >
          <span>{label}</span>
        </CrumbButton>
      ),
    });
  }

  if (props.showDropdownClubes) {
    const clubTxt = resumen.club == ' Todos' ? '' : resumen.club;
    const clubObj = props.clubes?.find((c: any) => c.clubId === props.clubId);
    const label = clubTxt || 'Clubes';

    crumbs.push({
      key: 'club',
      text: label,
      onClick: (ev, item) => {
        ev?.preventDefault();
        ev?.stopPropagation();
        exitSummaryTo('club');
      },
      onRender: () => (
        <CrumbButton
          title={label}
          onClick={(ev) => {
            ev.preventDefault();
            ev?.stopPropagation();
            exitSummaryTo('club');
          }}
        >
          {/* Escudo (si hay) */}
          {clubObj?.clubEscudo ? (
            <img
              src={clubObj.clubEscudo}
              alt={clubObj.club}
              style={{
                width: 18,
                height: 18,
                objectFit: 'contain',
                borderRadius: 3,
                boxShadow: '0 0 0 1px rgba(0,0,0,0.06)',
              }}
            />
          ) : null}
          <span>{label}</span>
        </CrumbButton>
      ),
    });
  }

  if (!federacionDefault) {
    const label = resumen.federacion || 'Federación';
    crumbs.push({
      key: 'federacion',
      text: label,
      onClick: (ev, item) => {
        ev?.preventDefault();
        ev?.stopPropagation();
        exitSummaryTo('federacion');
      },
      onRender: () => (
        <CrumbButton
          title={label}
          onClick={(ev) => {
            ev.preventDefault();
            ev?.stopPropagation();
            exitSummaryTo('federacion');
          }}
        >
          <span>{label}</span>
        </CrumbButton>
      ),
    });
  }

  if (props.menuFilters.federacionId) {
    const letraRama = (resumen.rama ?? '').toUpperCase().startsWith('F')
      ? 'F'
      : (resumen.rama ?? '').toUpperCase().startsWith('M')
      ? 'M'
      : undefined;

    const ramaLabel = letraRama === 'F' ? 'Femenino' : letraRama === 'M' ? 'Masculino' : 'Rama';

    crumbs.push({
      key: 'rama',
      text: ramaLabel,
      onClick: (ev, item) => {
        ev?.preventDefault();
        ev?.stopPropagation();
        exitSummaryTo('rama');
      },
      onRender: () => (
        <CrumbButton
          onClick={(ev) => {
            ev.preventDefault();
            ev?.stopPropagation();
            exitSummaryTo('rama');
          }}
          aria-label={letraRama === 'F' ? 'Femenino' : 'Masculino'}
          title={letraRama === 'F' ? 'Femenino' : 'Masculino'}
        >
          {letraRama ? <RamaIcon tipo={letraRama as 'F' | 'M'} /> : null}
          <span>{ramaLabel}</span>
        </CrumbButton>
      ),
    });
  }

  if (props.menuFilters.rama) {
    const raw = resumen.categoria || 'Categoría';
    const catLabel = raw.replace(/\s*\(\d+\)\s*$/, '');

    crumbs.push({
      key: 'categoria',
      text: catLabel,
      onClick: (ev, item) => {
        ev?.preventDefault();
        ev?.stopPropagation();
        exitSummaryTo('categoria');
      },
      onRender: () => (
        <CrumbButton
          title={catLabel}
          onClick={(ev) => {
            ev.preventDefault();
            ev.stopPropagation();
            exitSummaryTo('categoria');
          }}
        >
          <span>{catLabel}</span>
        </CrumbButton>
      ),
    });
  }
  // Wrapper animado para esconder sombras al colapsar todo
  const EASE = 'cubic-bezier(0.2, 0.8, 0.2, 1)';
  const [wrapMaxH, setWrapMaxH] = React.useState<number>(9999);
  const filtersWrapRef = React.useRef<HTMLDivElement | null>(null);

  React.useLayoutEffect(() => {
    const el = filtersWrapRef.current;
    if (!el) return;

    if (summaryOnly) {
      const h = el.scrollHeight;
      setWrapMaxH(h);
      requestAnimationFrame(() => setWrapMaxH(0));
    } else {
      const h = el.scrollHeight;
      setWrapMaxH(h);
      const t = setTimeout(() => setWrapMaxH(9999), 480);
      return () => clearTimeout(t);
    }
  }, [summaryOnly]);

  return (
    <Stack tokens={{ childrenGap: 12 }}>
      {/* Breadcrumb (step de resumen) */}
      <StepCard
        title="Selección"
        open={true}
        onToggle={(nextOpen: boolean) => {
          // Si colapsan manualmente el resumen, abrir el primer paso visible
          if (!nextOpen) exitSummaryTo(visibleSteps[0] ?? 'club');
        }}
        headerDisplay={false}
      >
        <Breadcrumb
          items={crumbs}
          styles={{
            root: { fontSize: theme.fonts.mediumPlus.fontSize, lineHeight: 18 },
            item: { fontSize: theme.fonts.mediumPlus.fontSize },
            itemLink: { fontSize: theme.fonts.mediumPlus.fontSize, padding: '2px 4px' },
            chevron: { fontSize: 10, margin: '0 4px' },
            overflowButton: { fontSize: 12, height: 24, width: 24 },
          }}
        />
      </StepCard>

      <div
        ref={filtersWrapRef}
        style={{
          overflow: 'hidden',
          maxHeight: wrapMaxH,
          transition: `max-height 480ms ${EASE}`,
          willChange: 'max-height',
        }}
      >
        {/* Renderizamos **solo** el paso actual */}
        {!federacionDefault && paso === 'federacion' && (
          <StepCard
            title="Federación"
            open={true}
            summary={resumen.federacion}
            onEdit={() => setPaso('federacion')}
            onToggle={toggleStep('federacion')}
            headerDisplay={!summaryOnly}
          >
            <Dropdown
              selectedKey={props.menuFilters.federacionId}
              onChange={(e, opt) => {
                onChange(CRITERIO_FEDERACION, String(opt!.key));
                setPaso('temporada');
              }}
              placeholder="Federación"
              options={props.federaciones}
              dropdownWidth="auto"
            />
          </StepCard>
        )}
        <Spacer />

        {props.showDropdownTemporada && paso === 'temporada' && (
          <StepCard
            title="Temporada"
            open={true}
            summary={resumen.temporada}
            onEdit={() => setPaso('temporada')}
            onToggle={toggleStep('temporada')}
            headerDisplay={!summaryOnly}
          >
            <Dropdown
              selectedKey={props.menuFilters.temporadaId}
              onChange={(e, opt) => {
                onChange(CRITERIO_TEMPORADA, String(opt!.key));
                setPaso('club');
              }}
              placeholder="Temporada"
              options={props.temporadas}
            />
          </StepCard>
        )}
        <Spacer />

        {props.showDropdownClubes && paso === 'club' && (
          <StepCard
            title="Club"
            open={true}
            summary={resumen.club}
            onEdit={() => setPaso('club')}
            onToggle={toggleStep('club')}
            headerDisplay={!summaryOnly}
          >
            <Dropdown
              selectedKey={props.clubId}
              onChange={(e, opt) => {
                props.setClubId(String(opt!.key));
                onChange(CRITERIO_TEMPORADA, props.menuFilters.temporadaId);
                setPaso('rama');
              }}
              placeholder="Club"
              options={props.clubes
                .sort((a: any, b: any) => a.club.localeCompare(b.club))
                .map((c: any) => ({ key: c.clubId, text: c.club }))}
            />
          </StepCard>
        )}
        <Spacer />

        {props.menuFilters.federacionId && paso === 'rama' && (
          <StepCard
            title="Rama"
            open={true}
            summary={resumen.rama}
            onEdit={() => setPaso('rama')}
            onToggle={toggleStep('rama')}
            headerDisplay={!summaryOnly}
          >
            <RamaButtons
              value={props.menuFilters.rama} // ✅ Usar directamente props
              options={props.ramas}
              isDisabledForKey={(key: string) => !props.clubHasRama(key)}
              onSelect={(key: string) => {
                console.log('rama seleccionada:', key);
                onChange(CRITERIO_RAMA, key);
                setPaso('categoria');
              }}
            />
          </StepCard>
        )}

        <Spacer />

        {props.menuFilters.rama && paso === 'categoria' && (
          <StepCard
            title="Categoría"
            open={true}
            summary={resumen.categoria}
            onEdit={() => setPaso('categoria')}
            onToggle={toggleStep('categoria')}
            headerDisplay={!summaryOnly}
          >
            <CategoriaButtons
              value={props.menuFilters.categoriaId}
              options={props.categorias}
              onSelect={(key: string) => {
                onChange(CRITERIO_CATEGORIA, key);
                setPaso(undefined);
              }}
              clubHasCategoria={(key: string) => props.clubHasCategoria(String(key))}
              clubHasCategoriaName={(key: string, text: string) =>
                props.clubHasCategoriaName(String(key), text)
              }
              renderOptionCategoria={(opt: any, selected: boolean) =>
                onRenderOptionCategoria ? onRenderOptionCategoria(opt as any) : undefined
              }
            />
          </StepCard>
        )}
        <Spacer />
      </div>
    </Stack>
  );
}

export default VerticalFilterStack;
